import { GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';
import { ReactNode, useCallback, useState } from 'react';
import { TableActionsContext, TableContext } from '../../context';
import { OnSelectionModelChangeFunction } from '../../context/types';

export interface TableContextProviderProps {
  readonly children: ReactNode;
  readonly customSelectionModel?: Map<GridRowId, GridValidRowModel>;
  readonly isMultiselect?: boolean;
  readonly isReloading?: boolean;
  readonly keepPagination?: boolean;
  readonly onChangeSelectionModel?: OnSelectionModelChangeFunction;
  readonly onClickCollapseIcon?: (value: GridRowId) => void;
  readonly onClickMoreIcon?: (value: GridRowId) => void;
}

export const TableContextProvider = ({
  isMultiselect: isMultiselectFromProps,
  onClickMoreIcon,
  children,
  onChangeSelectionModel,
  customSelectionModel,
  keepPagination,
}: TableContextProviderProps) => {
  const [isMultiselect, setIsMultiselect] = useState<boolean>(!!isMultiselectFromProps);
  const [selectionModel, setSelectionModel] = useState<Map<GridRowId, GridValidRowModel>>(new Map());

  const onSelectionModelChange: OnSelectionModelChangeFunction = useCallback((selectedRows, { api }) => {
    setSelectionModel((prevSelectedRows) => {
      const items: [GridRowId, GridValidRowModel][] = selectedRows.map((id) => {
        const item = (api?.getRow(id) as GridValidRowModel) ?? (prevSelectedRows.get(id) as GridValidRowModel);
        return [id, item];
      });
      return new Map(items);
    });
  }, []);

  const onClearSelection = useCallback(() => {
    setSelectionModel(new Map());
  }, []);

  return (
    <TableContext.Provider
      value={{
        isMultiselect,
        selectionModel: customSelectionModel ?? selectionModel,
        onClearSelection,
        keepPagination,
      }}
    >
      <TableActionsContext.Provider
        value={{
          setIsMultiselect,
          onClickMoreIcon,
          onSelectionModelChange: onChangeSelectionModel ?? onSelectionModelChange,
        }}
      >
        {children}
      </TableActionsContext.Provider>
    </TableContext.Provider>
  );
};
